(function () {
  'use strict';

  /* Node-safe: bootstrap is gated on isBrowser (same pattern as oem-search-engine.js).
     The undercoat cart-merge logic is shared and lives in assets/oem-undercoat-atc.js. */
  var isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';
  if (isBrowser && window.__OEMPDPBlocksInitialized) return;
  if (isBrowser) window.__OEMPDPBlocksInitialized = true;

  /* ─── ensureInView: shared viewport-shift helper ─────────────────
   * Owner requirement: whenever a section/card/drawer opens, the viewport
   * must shift so the newly-revealed content is actually visible — never
   * left below (or under the sticky header, above) the fold.
   *
   * Call this AFTER the reveal animation has settled (each call site owns
   * its own timing — this helper does not itself wait/animate-in). It:
   *   - measures the sticky header's live rendered height via #Page-Header
   *     (never hardcoded — accounts for shrink/scroll states)
   *   - no-ops if `el` is already fully within the viewport
   *   - otherwise smooth-scrolls the minimal distance to bring `el`'s top
   *     (+~16px margin) into view, instantly when prefers-reduced-motion
   *   - is only ever invoked from user-interaction handlers (click/open),
   *     never from page-load code, so it naturally never fires on initial load
   * Exposed on window so assets/oem-build-kit.js and the inline listener in
   * snippets/oem-touchup-compare.liquid (separate script contexts) can call
   * the SAME one implementation rather than each rolling their own.
   */
  function ensureInView(el) {
    if (!isBrowser || !el) return;

    var headerEl = document.getElementById('Page-Header');
    var headerHeight = headerEl ? Math.max(0, headerEl.getBoundingClientRect().bottom) : 0;
    var margin = 16;
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    var rect = el.getBoundingClientRect();
    var fullyVisible = rect.top >= headerHeight && rect.bottom <= viewportHeight;
    if (fullyVisible) return;

    var targetScrollY = window.scrollY + rect.top - headerHeight - margin;
    var prefersReducedMotion = !!(
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );

    window.scrollTo({
      top: Math.max(0, targetScrollY),
      behavior: prefersReducedMotion ? 'auto' : 'smooth'
    });
  }

  if (isBrowser) {
    window.ensureInView = ensureInView;
  }

  /* ─── Drawer Panel Expand / Collapse ──────────────────────────── */

  function initDrawerPanels(scope) {
    var triggers = scope.querySelectorAll('[data-oem-drawer-trigger]');
    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var drawer = trigger.closest('[data-oem-drawer]');
        if (!drawer) return;
        var isOpen = drawer.getAttribute('data-open') === 'true';
        var willOpen = !isOpen;
        drawer.setAttribute('data-open', willOpen ? 'true' : 'false');

        if (willOpen) {
          /* In-page expand (currently: vehicle-compat; also covers any
             oem-accessories-trigger block set to action_type=expand). The
             --oem-transition grid-template-rows reveal is 300ms — wait for
             it to settle before measuring/scrolling. The link/overlay
             variant of oem-accessories-trigger (Application Guide,
             Recommended Accessories) uses onclick to open a side drawer and
             never reaches this handler — intentionally skipped, per the
             "don't shift the viewport for overlay drawers" rule. */
          setTimeout(function () { ensureInView(drawer); }, 320);
        }
      });
    });
  }

  /* ─── FAQ Accordion ───────────────────────────────────────────── */

  function initFAQAccordions(scope) {
    var faqContainers = scope.querySelectorAll('[data-oem-faq]');
    faqContainers.forEach(function (container) {
      var allowMultiple = container.getAttribute('data-allow-multiple') === 'true';
      var triggers = container.querySelectorAll('[data-oem-faq-trigger]');

      triggers.forEach(function (trigger) {
        trigger.addEventListener('click', function () {
          var item = trigger.closest('.oem-faq-item');
          if (!item) return;

          var isOpen = item.getAttribute('data-open') === 'true';

          if (!allowMultiple && !isOpen) {
            var siblings = container.querySelectorAll('.oem-faq-item[data-open="true"]');
            siblings.forEach(function (sibling) {
              if (sibling !== item) {
                sibling.setAttribute('data-open', 'false');
              }
            });
          }

          item.setAttribute('data-open', isOpen ? 'false' : 'true');
        });
      });
    });
  }

  /* ─── Color Search (Drawer Mode) ─────────────────────────────── */

  function initColorSearchDrawer(scope) {
    var els = scope.querySelectorAll('[data-oem-color-search="drawer"]');
    els.forEach(function (el) {
      var btn = el.querySelector('button');
      if (!btn) return;

      btn.addEventListener('click', function () {
        if (!(window.NavFinderDrawer && typeof window.NavFinderDrawer.open === 'function')) {
          console.warn('[oem-pdp-blocks] NavFinderDrawer not available — cannot open color search drawer.');
          return;
        }
        window.NavFinderDrawer.open('color');
        /* Mini-search: carry any typed query into the drawer (same flow as the
           Enter handler in initColorSearchInput). */
        var input = el.querySelector('[data-oem-color-search-input]');
        var query = input ? input.value.trim() : '';
        if (!query) return;
        setTimeout(function () {
          var drawerInput = document.querySelector('.nf-drawer [data-nf-color-input]');
          if (drawerInput) {
            drawerInput.value = query;
            drawerInput.dispatchEvent(new Event('input', { bubbles: true }));
            drawerInput.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true, cancelable: true }));
          }
        }, 350);
      });
    });
  }

  /* ─── Color Search Input ─────────────────────────────────────── */

  function initColorSearchInput(scope) {
    var inputs = scope.querySelectorAll('[data-oem-color-search-input]');
    inputs.forEach(function (input) {
      input.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter') return;
        e.preventDefault();

        var query = input.value.trim();
        if (!query) return;

        if (window.NavFinderDrawer && typeof window.NavFinderDrawer.open === 'function') {
          window.NavFinderDrawer.open('color');

          /* After drawer opens, populate its color input with the query */
          setTimeout(function () {
            var drawerInput = document.querySelector('.nf-drawer [data-nf-color-input]');
            if (drawerInput) {
              drawerInput.value = query;
              drawerInput.dispatchEvent(new Event('input', { bubbles: true }));
              /* Trigger the search form submit */
              var drawerForm = drawerInput.closest('form');
              if (drawerForm) {
                drawerForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
              }
            }
          }, 300);
        } else {
          console.warn('[oem-pdp-blocks] NavFinderDrawer not available.');
        }
      });
    });
  }

  /* ─── Undercoat bundle: populate + native cart-merge ─────────── */
  /*
   * On tri-coat OEM colors (custom.undercoat_product metafield set) the alt PDP
   * promises the matched undercoat is auto-added "in the price". This block owns
   * BOTH halves natively — mcc.js (which defined <mcc-product-form> and its
   * onSubmitHandler → addUndercoatToCart()) is RETIRED / never loaded, so nothing
   * from mcc.js runs. We:
   * 1. Fetch undercoat variants + oem_image + price via Storefront API
   * 2. Populate the hidden form#undercoatAddToCart whenever the main variant changes
   * 3. Update the undercoat card visual to match the actual undercoat color
   * 4. Add undercoat price to the displayed total + show the "both included" badge
   * 5. FULFILL: intercept the main add-to-cart submit and batch the undercoat line
   *    into the SAME /cart/add.js request (attachUndercoatMergeInterceptor, below) —
   *    this replaces the dead MCCProductForm.addUndercoatToCart() path.
   */

  var STOREFRONT_TOKEN = '92a12a61fd6bbd940a9a841958fcb365';
  var STOREFRONT_SHOP = 'dipyourcar-com.myshopify.com';
  var STOREFRONT_API = 'https://' + STOREFRONT_SHOP + '/api/2025-07/graphql.json';
  var undercoatVariants = null;
  var undercoatImage = null;
  var undercoatProductTitle = null;
  var currentUndercoatMatch = null; /* currently matched undercoat variant (has .price) */
  var priceObserver = null;
  var isUpdatingPrice = false; /* re-entrancy guard for MutationObserver */

  /* ── Format price in cents to display string ──────────────── */
  function fmtPrice(cents) {
    if (typeof formatPriceAmount === 'function') {
      return formatPriceAmount(cents, 'USD');
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /* ── Update undercoat card visuals ────────────────────────── */
  function updateUndercoatCard() {
    if (!undercoatImage) return;

    var cardBg = document.querySelector('.oem-cc-card--under');
    var cardSwatch = cardBg ? cardBg.querySelector('.oem-cc-card__swatch') : null;
    var cardName = cardBg ? cardBg.querySelector('.oem-cc-card__name') : null;
    var cardCode = cardBg ? cardBg.querySelector('.oem-cc-card__code') : null;
    if (!cardBg) return;

    var bgStyle, swatchStyle;
    if (undercoatImage.indexOf('://') !== -1) {
      bgStyle = "url('" + undercoatImage + "') center/contain repeat";
      swatchStyle = "background: url('" + undercoatImage + "') center/contain repeat;";
    } else if (undercoatImage.indexOf('|') !== -1) {
      var rgb = undercoatImage.split('|').join(',');
      bgStyle = 'linear-gradient(145deg, rgb(' + rgb + '), rgba(' + rgb + ',0.8))';
      swatchStyle = 'background-color: rgb(' + rgb + ');';
    } else if (undercoatImage.indexOf(',') !== -1) {
      bgStyle = 'linear-gradient(145deg, rgb(' + undercoatImage + '), rgba(' + undercoatImage + ',0.8))';
      swatchStyle = 'background-color: rgb(' + undercoatImage + ');';
    } else {
      bgStyle = 'linear-gradient(145deg, ' + undercoatImage + ', ' + undercoatImage + ')';
      swatchStyle = 'background-color: ' + undercoatImage + ';';
    }

    cardBg.style.background = bgStyle;
    if (cardSwatch) cardSwatch.style.cssText = swatchStyle;
    if (undercoatProductTitle && cardName) cardName.textContent = undercoatProductTitle;
    if (cardCode) cardCode.textContent = 'Undercoat';
  }

  /* The blue "Undercoat included" info badge that used to be injected here
     was REMOVED (owner round 3): it duplicated the Color Confirmation
     undercoat swatch card, the undercoat STEP CARD in Paint
     Characteristics (the in-flow signal), and the green "Undercoat
     required" row. The price line (.oem-uc-price-breakdown) is separate
     and stays. */

  /* ── Price: add undercoat amount after server refresh ─────── */
  function addUndercoatToPrice() {
    if (!currentUndercoatMatch || !currentUndercoatMatch.price) return;
    if (isUpdatingPrice) return; /* prevent re-entrancy from MutationObserver */

    var priceEl = document.querySelector('[id^="Price-"]');
    if (!priceEl) return;

    /* The main price item — <b class="price-item">$XX.XX</b> */
    var priceItem = priceEl.querySelector('b.price-item');
    if (!priceItem) return;

    /* Read the BASE price — either from stored data attr (if we already modified)
       or parse the currently displayed text (fresh from server) */
    var mainCents;
    if (priceItem.dataset.oemBaseCents) {
      mainCents = parseInt(priceItem.dataset.oemBaseCents, 10);
    } else {
      var priceText = priceItem.textContent.trim();
      var numMatch = priceText.replace(/[^0-9.]/g, '');
      mainCents = Math.round(parseFloat(numMatch) * 100);
    }
    if (isNaN(mainCents) || mainCents <= 0) return;

    /* Undercoat price — Storefront API returns amount as string like "29.99" */
    var ucCents = Math.round(parseFloat(currentUndercoatMatch.price.amount) * 100);
    if (isNaN(ucCents) || ucCents <= 0) return;

    var totalCents = mainCents + ucCents;
    var totalFormatted = fmtPrice(totalCents);
    if (!totalFormatted) {
      totalFormatted = '$' + (totalCents / 100).toFixed(2);
    }

    /* Guard: pause observer while we mutate the DOM */
    isUpdatingPrice = true;

    /* Store the base price so subsequent calls don't re-add */
    priceItem.dataset.oemBaseCents = mainCents;
    priceItem.textContent = totalFormatted;

    /* Add or update the "Includes undercoat" label */
    var breakdownId = 'oem-uc-price-breakdown';
    var existing = priceEl.querySelector('#' + breakdownId);
    if (!existing) {
      existing = document.createElement('div');
      existing.id = breakdownId;
      existing.className = 'oem-uc-price-breakdown';
      priceItem.parentNode.insertBefore(existing, priceItem.nextSibling);
    }
    existing.innerHTML = '<span class="oem-uc-pb__label">Includes Required Undercoat</span>';

    /* Reveal the price (was hidden on click to prevent flicker) */
    priceEl.style.opacity = '';

    console.log('[oem] Price updated: ' + fmtPrice(mainCents) + ' + ' + fmtPrice(ucCents) + ' = ' + totalFormatted);

    /* Release guard after microtask so observer callbacks queued by our
       DOM changes are still blocked */
    requestAnimationFrame(function () { isUpdatingPrice = false; });
  }

  /* Owner 2026-07: undercoat-inclusive pricing on every size ROW — combined
     total + an "incl. undercoat" chip on the row border, matched size-for-size
     (rows with no matching undercoat size — e.g. the 2oz 1K-gloss touch-up —
     stay untouched, which is honest: they don't need an undercoat). */
  function renderSizeRowChips() {
    if (!undercoatVariants || !window.OEMUndercoatATC) return;
    var rows = document.querySelectorAll('.ov-size');
    Array.prototype.forEach.call(rows, function (row) {
      var priceEl = row.querySelector('.ov-size-price');
      if (!priceEl) return;
      var sizeVal = row.getAttribute('data-size');
      var uv = window.OEMUndercoatATC.matchUndercoatVariant([sizeVal], undercoatVariants);
      var banner = row.querySelector('.ov-size-banner');
      if (!uv || !uv.price || !uv.price.amount) { if (banner) banner.remove(); return; }
      if (!priceEl.dataset.baseCents) {
        var base = (priceEl.textContent || '').replace(/[^0-9.]/g, '');
        priceEl.dataset.baseCents = String(Math.round(parseFloat(base || '0') * 100));
      }
      var total = parseInt(priceEl.dataset.baseCents, 10) + Math.round(parseFloat(uv.price.amount) * 100);
      priceEl.textContent = fmtPrice(total) || ('$' + (total / 100).toFixed(2));
      /* SunGod-style strip fused to the card bottom (owner 2026-07). */
      if (!banner) {
        banner = document.createElement('span');
        banner.className = 'ov-size-banner';
        banner.textContent = 'Includes Required Undercoat';
        row.appendChild(banner);
      }
    });
  }

  function watchPriceElement() {
    var priceEl = document.querySelector('[id^="Price-"]');
    if (!priceEl || priceObserver) return;

    priceObserver = new MutationObserver(function () {
      if (isUpdatingPrice) return; /* skip mutations caused by our own updates */
      /* refreshPriceFromServer replaces innerHTML, which creates new elements
         without data-oem-base-cents — so the fresh text is the real base price */
      setTimeout(addUndercoatToPrice, 50);
    });
    priceObserver.observe(priceEl, { childList: true, subtree: true, characterData: true });
  }

  /* ── Match & populate undercoat form ──────────────────────── */
  function updateUndercoatForm(selectedOptions) {
    var ucForm = document.querySelector('form#undercoatAddToCart');
    if (!ucForm || !undercoatVariants) return;

    var ucIdInput = ucForm.querySelector('input[name="id"]');
    var ucForInput = ucForm.querySelector('input[name="properties[Undercoat For]"]');
    if (!ucIdInput) return;

    /* Shared positional matcher — exact mcc.js updateUndercoatId rule
       (see assets/oem-undercoat-atc.js). */
    var match = (window.OEMUndercoatATC)
      ? window.OEMUndercoatATC.matchUndercoatVariant(selectedOptions, undercoatVariants)
      : null;

    currentUndercoatMatch = match;

    if (match) {
      ucIdInput.value = match.id.split('/').pop();

      var mainForm = document.querySelector('form[data-type="add-to-cart-form"]');
      var colorName = '', colorCode = '';
      if (mainForm) {
        var cnInput = mainForm.querySelector('input[name="properties[Color Name]"]');
        var ccInput = mainForm.querySelector('input[name="properties[Color Code]"]');
        if (cnInput) colorName = cnInput.value;
        if (ccInput) colorCode = ccInput.value;
      }
      if (ucForInput) {
        ucForInput.value = ((colorName || '') + ' / ' + (colorCode || '')).trim();
      }

      console.log('[oem] Undercoat matched: variant', ucIdInput.value, match.title, '| price:', match.price && match.price.amount);
      /* Apply immediately — the price-node MutationObserver only covers
         re-renders, and the OEM size selector doesn't re-render the price. */
      addUndercoatToPrice();
      renderSizeRowChips();
    } else {
      ucIdInput.value = '';
      if (ucForInput) ucForInput.value = '';
      /* Remove the price breakdown if no match */
      var bd = document.querySelector('#oem-uc-price-breakdown');
      if (bd) bd.remove();
      console.warn('[oem] No matching undercoat variant for options:', selectedOptions);
    }
  }

  /* Fallback when no size button is pressed yet (fresh page load): derive the
     options from the main form's CURRENT variant via the ov-variants JSON the
     size selector renders — so the top price is undercoat-inclusive from the
     first paint, not only after a click. */
  function getOptionsFromMainVariant() {
    var idInput = document.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
    if (!idInput) return [];
    var dataEl = document.querySelector('script[id^="ov-variants-"]');
    if (!dataEl) return [];
    try {
      var variants = JSON.parse(dataEl.textContent);
      var target = null;
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(idInput.value)) { target = variants[i]; break; }
      }
      /* Nothing selected yet → the theme displays the FIRST AVAILABLE variant's
         price, so combine against that same variant. */
      if (!target) {
        for (var j = 0; j < variants.length; j++) {
          if (variants[j].available) { target = variants[j]; break; }
        }
      }
      if (target) {
        return [target.option1, target.option2, target.option3]
          .filter(function (o) { return o != null && o !== ''; });
      }
    } catch (e) { /* fall through */ }
    return [];
  }

  function syncUndercoat(mccForm) {
    if (!undercoatVariants) return;
    var opts = getCurrentOptions(mccForm);
    if (!opts.length) opts = getOptionsFromMainVariant();
    if (opts.length > 0) {
      updateUndercoatForm(opts);
    }
  }

  /* ─── Native undercoat cart-merge (replaces dead MCCProductForm path) ───
   * The interceptor + payload logic is SHARED with the classic pre-made-oem PDP and
   * lives in assets/oem-undercoat-atc.js (window.OEMUndercoatATC), loaded by
   * sections/mcc-main-product-alt.liquid before this file. One batched /cart/add.js
   * items[] request (never a wrapped FormData / two sequential POSTs — the
   * historical duplicate-cart bug); native-submit fallback on any failure. */

  /* ── Init ─────────────────────────────────────────────────── */
  function initUndercoatBundle() {
    /* Undercoat product id: prefer the color-confirmation card (alt PDP), fall back
       to the legacy mcc-product-form attribute if a surface still renders it. */
    var confirmEl = document.querySelector('.oem-color-confirm[data-undercoat-product-id]');
    var legacyForm = document.querySelector('mcc-product-form[data-undercoat-product-id]');
    var undercoatId = String(
      (confirmEl && confirmEl.getAttribute('data-undercoat-product-id')) ||
      (legacyForm && legacyForm.getAttribute('data-undercoat-product-id')) || ''
    ).trim();
    if (!undercoatId) return;

    /* getCurrentOptions falls back to the .ov-size / .sv-size buttons when the host
       has no .options, so any stable host element works. */
    var mccForm = document.querySelector('mcc-product-form') || confirmEl || document.body;

    /* Bind the fulfill (cart-merge) interceptor up front — it stays inert until the
       undercoat form is populated, then batches the undercoat into the main add. */
    if (window.OEMUndercoatATC) {
      window.OEMUndercoatATC.attach();
    } else {
      console.warn('[oem] oem-undercoat-atc.js missing — undercoat will NOT be auto-added');
    }

    console.log('[oem] initUndercoatBundle: undercoat product ID =', undercoatId);

    /* Fetch undercoat variant data + oem_image + price via Storefront GraphQL */
    var query = 'query getUndercoatVariants { node(id: "gid://shopify/Product/' + undercoatId + '") { ... on Product { id title metafield(namespace: "custom", key: "oem_image") { value } variants(first: 100) { edges { node { id title price { amount currencyCode } selectedOptions { name value } } } } } } }';

    fetch(STOREFRONT_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
      },
      body: JSON.stringify({ query: query })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var node = data && data.data && data.data.node;
        if (!node || !node.variants) {
          console.warn('[oem] Failed to load undercoat product data');
          return;
        }

        undercoatVariants = node.variants.edges.map(function (e) { return e.node; });
        undercoatProductTitle = node.title || 'Luna Undercoat';
        undercoatImage = (node.metafield && node.metafield.value) || null;

        console.log('[oem] Undercoat loaded:', undercoatVariants.length, 'variants, image:', undercoatImage);

        updateUndercoatCard();
        renderSizeRowChips();
        syncUndercoat(mccForm);
        /* Apply combined price for the initially selected variant.
           Must run AFTER syncUndercoat sets currentUndercoatMatch. */
        addUndercoatToPrice();
        /* THEN start observing — observer handles all subsequent variant changes */
        watchPriceElement();
        /* The size selector applies its default selection asynchronously —
           retry until a match lands so the initial price is combined too. */
        [400, 1000, 2000].forEach(function (t) {
          setTimeout(function () { if (!currentUndercoatMatch) syncUndercoat(mccForm); }, t);
        });
      })
      .catch(function (err) {
        console.warn('[oem] Undercoat fetch error:', err);
      });

    /* Size-variant-selector uses button clicks (.sv-size).
       Hide the price immediately on click so the base price doesn't flash
       before our combined price is applied. The observer will reveal it
       once addUndercoatToPrice runs after refreshPriceFromServer completes. */
    document.addEventListener('click', function (e) {
      if (!undercoatVariants) return;
      var btn = e.target.closest('.sv-size, .ov-size');
      if (!btn) return;
      var priceEl = document.querySelector('[id^="Price-"]');
      if (priceEl) priceEl.style.opacity = '0';
      setTimeout(function () { syncUndercoat(mccForm); }, 150);
      /* Safety: reveal after 1.5s in case observer never fires */
      setTimeout(function () {
        var p = document.querySelector('[id^="Price-"]');
        if (p && p.style.opacity === '0') p.style.opacity = '';
      }, 1500);
    });
  }

  function getCurrentOptions(mccForm) {
    if (mccForm.options && mccForm.options.length > 0) {
      return mccForm.options;
    }

    var svButtons = document.querySelectorAll('.sv-size[aria-pressed="true"]');
    if (svButtons.length > 0) {
      var options = [];
      svButtons.forEach(function (btn) {
        var val = btn.getAttribute('data-option-value');
        if (val) options.push(val);
      });
      if (options.length > 0) return options;
    }

    var ovButton = document.querySelector('.ov-size[aria-pressed="true"]');
    if (ovButton) {
      var sizeValue = ovButton.getAttribute('data-size') || ovButton.getAttribute('data-option-value');
      if (sizeValue) return [sizeValue];
    }

    var options2 = [];
    var actualForm = mccForm.querySelector('form[data-type="add-to-cart-form"]');
    if (!actualForm) return options2;
    var fieldsets = actualForm.querySelectorAll('fieldset, .product-form__input');
    fieldsets.forEach(function (fs) {
      var checked = fs.querySelector('input:checked');
      if (checked) { options2.push(checked.value); }
      else { var sel = fs.querySelector('select'); if (sel) options2.push(sel.value); }
    });
    return options2;
  }

  /* ─── Vehicle Compatibility (MCC API) ─────────────────────────── */

  var VC_API_BASE = 'https://dyc-ai-search.vercel.app';

  /* Resolve Shopify asset URL base from any asset already on the page */
  function getAssetUrlBase() {
    var el = document.querySelector('link[href*="/assets/"]') ||
             document.querySelector('script[src*="/assets/"]');
    if (el) {
      var url = el.href || el.src || '';
      var idx = url.indexOf('/assets/');
      if (idx !== -1) return url.substring(0, idx + '/assets/'.length);
    }
    /* Fallback: look for data attribute set by make-model-year-finder */
    var finder = document.querySelector('[data-asset-url-base]');
    if (finder) return finder.getAttribute('data-asset-url-base');
    return '';
  }

  /* Brand names → logo slugs. Sorted longest-first so multi-word brands match before short ones. */
  var BRAND_MAP = [
    ['Alfa Romeo', 'alfa-romeo'], ['American Motors', 'american-motors'],
    ['Aston Martin', 'aston-martin'], ['General Motors', 'general-motors'],
    ['Jaguar Land Rover', 'jaguar-land-rover'], ['Land Rover', 'jaguar-land-rover'],
    ['Mercedes-Benz', 'mercedes-benz'], ['Mercedes Benz', 'mercedes-benz'],
    ['Mercedes', 'mercedes-benz'], ['Rolls-Royce', 'rolls-royce'],
    ['Rolls Royce', 'rolls-royce'], ['Volkswagen', 'volkswagen'],
    ['Audi', 'audi'], ['Bentley', 'bentley'], ['BMW', 'bmw'],
    ['Bugatti', 'bugatti'], ['Buick', 'buick'], ['Cadillac', 'cadillac'],
    ['Chevrolet', 'chevrolet'], ['Chrysler', 'chrysler'], ['Dodge', 'dodge'],
    ['Ferrari', 'ferrari'], ['Fiat', 'fiat'], ['Fisker', 'fisker'],
    ['Ford', 'ford'], ['Franklin', 'franklin'], ['Freightliner', 'freightliner'],
    ['Genesis', 'genesis'], ['GMC', 'gmc'], ['Honda', 'honda'],
    ['Hummer', 'hummer'], ['Hyundai', 'hyundai'], ['Infiniti', 'infiniti'],
    ['Jaguar', 'jaguar'], ['Jeep', 'jeep'], ['Karma', 'karma'],
    ['Kenworth', 'kenworth'], ['Kia', 'kia'], ['KTM', 'ktm'],
    ['Lamborghini', 'lamborghini'], ['Lexus', 'lexus'], ['Lotus', 'lotus'],
    ['Lucid', 'lucid'], ['Maserati', 'maserati'], ['Mazda', 'mazda'],
    ['Mercury', 'mercury'], ['Mini', 'mini'], ['Mitsubishi', 'mitsubishi'],
    ['Nissan', 'nissan'], ['Oldsmobile', 'oldsmobile'], ['Peterbilt', 'peterbilt'],
    ['Plymouth', 'plymouth'], ['Pontiac', 'pontiac'], ['Porsche', 'porsche'],
    ['Ram', 'ram'], ['Rivian', 'rivian'], ['Scion', 'toyota'],
    ['Subaru', 'subaru'], ['Suzuki', 'suzuki'], ['Tesla', 'tesla'],
    ['Toyota', 'toyota'], ['Trion', 'trion'], ['Volvo', 'volvo'], ['VW', 'volkswagen']
  ];

  /**
   * Extract brand slug from a product title like "Toyota Magnetic Grey M. 1G3 | Luna OEM Basecoat".
   * Matches against known brand names (longest first) at the start of the title.
   * Returns the logo slug or null.
   */
  function extractBrandSlug(title) {
    if (!title) return null;
    var t = title.trim();
    for (var i = 0; i < BRAND_MAP.length; i++) {
      var name = BRAND_MAP[i][0];
      /* Case-insensitive startsWith, then ensure next char is a space or end-of-string */
      if (t.length >= name.length &&
          t.substring(0, name.length).toLowerCase() === name.toLowerCase()) {
        var next = t.charAt(name.length);
        if (!next || next === ' ' || next === '-' || next === '|') {
          return BRAND_MAP[i][1];
        }
      }
    }
    return null;
  }

  function getBrandLogoUrl(slug, assetBase) {
    if (!slug) return null;
    return assetBase + 'brand-logo-' + slug + '.png';
  }

  /* Condense year array into ranges: [2018,2019,2020,2022,2023] → "2018–2020, 2022–2023" */
  function formatYearRanges(years) {
    if (!years || years.length === 0) return '';
    var sorted = years.slice().sort(function (a, b) { return a - b; });
    var ranges = [];
    var start = sorted[0], end = sorted[0];
    for (var i = 1; i < sorted.length; i++) {
      if (sorted[i] === end + 1) {
        end = sorted[i];
      } else {
        ranges.push(start === end ? String(start) : start + '–' + end);
        start = end = sorted[i];
      }
    }
    ranges.push(start === end ? String(start) : start + '–' + end);
    return ranges.join(', ');
  }

  function initVehicleCompat() {
    var el = document.querySelector('[data-oem-vehicle-compat]');
    if (!el) return;

    var cloudId = (el.getAttribute('data-cloud-id') || '').trim();
    var colorCode = (el.getAttribute('data-color-code') || '').trim();
    var brand = (el.getAttribute('data-brand') || '').trim();
    var productTitle = (el.getAttribute('data-product-title') || '').trim();
    var list = el.querySelector('[data-vc-list]');
    var subtitle = el.querySelector('[data-vc-subtitle]');
    var logoWrap = el.querySelector('[data-vc-brand-logo]');
    var loading = el.querySelector('[data-vc-loading]');

    /* No color code → hide the block entirely */
    if (!colorCode) {
      el.style.display = 'none';
      return;
    }

    var assetBase = getAssetUrlBase();

    /* Extract brand from product title (always single brand, e.g. "Toyota Magnetic Grey M. 1G3 | Luna OEM Basecoat") */
    var brandSlug = extractBrandSlug(productTitle);

    /* Set brand logo immediately (before API call) */
    if (brandSlug && logoWrap && !logoWrap.querySelector('img')) {
      var logoUrl = getBrandLogoUrl(brandSlug, assetBase);
      if (logoUrl) {
        logoWrap.innerHTML = '<img src="' + logoUrl + '" alt="" width="40" height="40" style="object-fit:contain;" loading="lazy" onerror="this.style.display=\'none\'">';
      }
    }

    /* Build API URL */
    var apiUrl = VC_API_BASE + '/api/vehicle-compat?code=' + encodeURIComponent(colorCode);
    if (cloudId) apiUrl += '&cloud_id=' + encodeURIComponent(cloudId);
    if (brand) apiUrl += '&brand=' + encodeURIComponent(brand);

    fetch(apiUrl)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (loading) loading.style.display = 'none';

        var vehicles = data.vehicles || [];

        if (vehicles.length === 0) {
          if (list) list.innerHTML = '<div class="oem-vehicle-compat__empty">No vehicle data available for this color</div>';
          return;
        }

        /* Update subtitle with count */
        if (subtitle) {
          subtitle.textContent = vehicles.length + ' compatible vehicle' + (vehicles.length > 1 ? 's' : '');
        }

        /* Use title-derived brand slug for all logos; fall back to API brand if title didn't match */
        var logoSlug = brandSlug || extractBrandSlug(data.brand);
        var logoSrc = getBrandLogoUrl(logoSlug, assetBase);

        /* Set trigger logo if not already set */
        if (logoSlug && logoWrap && !logoWrap.querySelector('img')) {
          if (logoSrc) {
            logoWrap.innerHTML = '<img src="' + logoSrc + '" alt="" width="40" height="40" style="object-fit:contain;" loading="lazy" onerror="this.style.display=\'none\'">';
          }
        }

        /* Render vehicle items */
        var html = '';
        for (var i = 0; i < vehicles.length; i++) {
          var v = vehicles[i];
          var yearsStr = formatYearRanges(v.years);

          html += '<div class="oem-vehicle-compat__item">';
          if (logoSrc) {
            html += '<img class="oem-vehicle-compat__logo" src="' + logoSrc + '" alt="" loading="lazy" onerror="this.style.display=\'none\'">';
          }
          html += '<span class="oem-vehicle-compat__text">' + v.model_name + '</span>';
          if (yearsStr) {
            html += '<span class="oem-vehicle-compat__years">' + yearsStr + '</span>';
          }
          html += '</div>';
        }

        if (list) list.innerHTML = html;

        console.log('[oem] Vehicle compat loaded:', vehicles.length, 'vehicles for', productTitle, '→', logoSlug);
      })
      .catch(function (err) {
        console.warn('[oem] Vehicle compat error:', err);
        if (loading) loading.style.display = 'none';
        if (list) list.innerHTML = '<div class="oem-vehicle-compat__empty">Unable to load vehicle data</div>';
      });
  }

  /* ─── Paint Characteristics: realtime V3 refinement (Task 23) ────────
   * Progressive enhancement over the server-rendered heuristic
   * (snippets/oem-sliders.liquid, Task 22 — name-suffix guess of paint
   * type from oem_color_name, e.g. " P." = Pearl). Once
   * window.OEMSearchEngine (assets/oem-search-engine.js) is available, look
   * the product's real color up by colorCode+brand and, on a CONFIDENT
   * match, re-render the display from the REAL finish (deriveFinish()'s
   * is_metallic/is_pearl/is_xirallic/is_candi/is_special_effect flags —
   * already resolved into `finish` by normalizeHit) instead of the guess.
   * Owner redesign pass: the display is segmented PIPS + word labels
   * (sparkle/difficulty) and a coats LAYER STACK — refinement swaps
   * filled-pip counts / words / the color-coats multiplier, not bar widths.
   * The heuristic render is always the first paint; this only refines it
   * after the fact, never blocks/replaces initial render. The undercoat
   * badge row and the stack's undercoat slab are untouched — metafield/
   * commerce truth, not V3's call.
   */

  /* Mirror of snippets/oem-sliders.liquid's per-type value table (Task 22
   * recon). NUMBERS MUST STAY IDENTICAL to that Liquid table — only the
   * classification INPUT differs (real V3 finish here vs. the name-suffix
   * heuristic there). Precedence (xirallic > candy > pearl > metallic >
   * special > solid) is NOT re-implemented here — `finish` on a normalized
   * V3 hit already resolves that precedence (deriveFinish() in
   * oem-search-engine.js), so this table is keyed straight off it.
   * Owner correction (round 3): COLOR coats are ×2–3 for EVERY finish — on
   * effect finishes (pearl/xirallic/candy/special) the 3rd coat is the
   * effect/drop coat, not extra color. `coats` is the color card's display
   * range; `effectCoat` toggles the card's effect-coat sub-note visibility.
   * Both mirror coats_range/effect_coat in the Liquid. */
  var OEM_PAINT_VALUES = {
    solid: { effect: 8, difficulty: 25, coats: '2–3', effectCoat: false, coatsLabel: '2–3 coats', coatsValue: 45 },
    metallic: { effect: 60, difficulty: 45, coats: '2–3', effectCoat: false, coatsLabel: '2–3 coats', coatsValue: 45 },
    pearl: { effect: 55, difficulty: 60, coats: '2–3', effectCoat: true, coatsLabel: '2–3 coats', coatsValue: 65 },
    special: { effect: 70, difficulty: 70, coats: '2–3', effectCoat: true, coatsLabel: '2–3 coats', coatsValue: 65 },
    candy: { effect: 45, difficulty: 85, coats: '2–3', effectCoat: true, coatsLabel: '2–3 coats', coatsValue: 65 },
    xirallic: { effect: 85, difficulty: 65, coats: '2–3', effectCoat: true, coatsLabel: '2–3 coats', coatsValue: 65 }
  };

  /* Tier mapping — MIRRORED from snippets/oem-sliders.liquid (owner
   * redesign): 0-100 table value → 5 discrete display tiers.
   * 1-20→1, 21-40→2, 41-60→3, 61-80→4, 81-100→5. Keep the two identical. */
  function oemTierFromValue(value) {
    var v = Number(value);
    if (!(v > 0)) return 1;
    return Math.max(1, Math.min(5, Math.ceil(v / 20)));
  }

  /* Difficulty tier words — MIRRORED from snippets/oem-sliders.liquid
   * (difficulty_word case). Keep identical. */
  var OEM_DIFFICULTY_WORDS = { 1: 'Beginner', 2: 'Easy', 3: 'Moderate', 4: 'Advanced', 5: 'Expert' };

  function oemNormalizeForCompare(s) {
    return String(s == null ? '' : s).trim().toLowerCase();
  }

  /* Brand match: case-insensitive, containment either direction. V3 brand
   * names are often abbreviated/aliased vs. the oem_brand metafield — e.g.
   * metafield "LAMBORGHINI" vs. V3 brand_name "LAM", or metafield
   * "CHEVROLET EUROPE | DAEWOO" (compound) vs. V3 brand_name "DAEW" — plain
   * equality would miss both (confirmed against the live V3 API, Task 23). */
  function oemBrandsMatch(a, b) {
    var na = oemNormalizeForCompare(a);
    var nb = oemNormalizeForCompare(b);
    if (!na || !nb) return false;
    return na.indexOf(nb) !== -1 || nb.indexOf(na) !== -1;
  }

  /* First hit with exact case-insensitive colorCode equality AND a brand
   * match. Same colorCode legitimately maps to different paint types across
   * brands (confirmed live: V3 code "51U" is metallic+pearl for brand DAEW
   * but plain solid for brand ISU/VAUX/SUZ) — brand is NOT optional.
   *
   * Task 24c fallback pass: under the server grouping contract
   * (docs/shopify-grouping-and-variants-explainer.md) a record's code can
   * survive only as an ALIAS on another record's alternative_color_codes
   * (the group's code union). The lookup itself now asks for group=false
   * (exact records), so the primary pass should normally hit — the second
   * pass catches codes the API only returns alias-side. Primary equality
   * always wins over an alias hit (pass order), and brand is still REQUIRED
   * in both passes (same 51U caution). */
  function oemFindConfidentMatch(colors, colorCode, brand) {
    var code = oemNormalizeForCompare(colorCode);
    if (!code || !colors) return null;
    var i, c;
    for (i = 0; i < colors.length; i++) {
      c = colors[i];
      if (oemNormalizeForCompare(c.colorCode) === code && oemBrandsMatch(c.brandName, brand)) {
        return c;
      }
    }
    for (i = 0; i < colors.length; i++) {
      c = colors[i];
      if (!oemBrandsMatch(c.brandName, brand)) continue;
      var alts = c.alternativeCodes || [];
      for (var j = 0; j < alts.length; j++) {
        if (oemNormalizeForCompare(alts[j]) === code) return c;
      }
    }
    return null;
  }

  function oemClamp(n, max) {
    return Math.min(n, max);
  }

  /* hasUndercoat mirrors the Liquid heuristic's own +10 difficulty / +15
   * coats / " + undercoat base" label bump (snippets/oem-sliders.liquid) —
   * applied here too so a real-color refinement never REGRESSES a tri-coat
   * product's display back below what the (correct) undercoat-aware
   * heuristic already showed. Undercoat truth itself is never re-derived
   * from V3 here — it's read from the already-rendered
   * `.oem-undercoat-indicator` badge (metafield/commerce truth), never
   * touched or replaced. */
  function oemComputeRefinedValues(finish, hasUndercoat) {
    var key = oemNormalizeForCompare(finish);
    var base = OEM_PAINT_VALUES[key] || OEM_PAINT_VALUES.solid;
    var out = {
      effect: base.effect,
      difficulty: base.difficulty,
      coatsLabel: base.coatsLabel,
      coatsValue: base.coatsValue
    };
    if (hasUndercoat) {
      out.difficulty = oemClamp(out.difficulty + 10, 95);
      out.coatsValue = oemClamp(out.coatsValue + 15, 100);
      out.coatsLabel = out.coatsLabel + ' + undercoat base';
    }
    return out;
  }

  /* Full display model for the redesigned block: tiers + words + coats
   * range, derived from the same value table (single numeric source of
   * truth) via the mirrored tier mapping. This — not the raw 0-100 values —
   * is what gets applied to the DOM and cached in sessionStorage. */
  function oemComputeRefinedDisplay(finish, hasUndercoat) {
    var key = oemNormalizeForCompare(finish);
    var known = !!OEM_PAINT_VALUES[key];
    var values = oemComputeRefinedValues(finish, hasUndercoat);
    var effectTier = oemTierFromValue(values.effect);
    var difficultyTier = oemTierFromValue(values.difficulty);
    var entry = OEM_PAINT_VALUES[key] || OEM_PAINT_VALUES.solid;
    return {
      finishWord: known ? key.charAt(0).toUpperCase() + key.slice(1) : 'Solid',
      effectTier: effectTier,
      difficultyTier: difficultyTier,
      difficultyWord: OEM_DIFFICULTY_WORDS[difficultyTier],
      coats: entry.coats,
      /* True on effect finishes: the 3rd coat is the effect/drop coat —
       * drives the color card's sub-note VISIBILITY only (the note's text
       * is merchant-editable in the block settings and never rewritten). */
      effectCoat: entry.effectCoat,
      coatsLabel: values.coatsLabel,
      /* Carried through so the apply step (and cache restores) keep the
       * undercoat step card in sync. In practice it always equals the
       * server-rendered truth — metafield-driven, V3 never overrides it. */
      hasUndercoat: !!hasUndercoat
    };
  }

  /* SR text equivalent for the coats step cards — MIRRORED from
   * snippets/oem-sliders.liquid (coats_sr assign). Keep wording identical
   * so a refined announcement matches the server-rendered one. */
  function oemCoatsSrText(coatsRange, hasUndercoat, effectCoat) {
    return 'Application steps, in order: '
      + (hasUndercoat ? 'undercoat base (added automatically), then ' : '')
      + 'color coats ×' + coatsRange
      + (effectCoat ? ' (the third coat adds the effect)' : '')
      + ', then 2K gloss clearcoat for final gloss and protection.';
  }

  var OEM_REFINE_CACHE_TTL_MS = 60 * 60 * 1000; /* 1h */

  function oemProductHandleFromLocation() {
    if (!isBrowser) return null;
    var m = /\/products\/([^/?#]+)/.exec(window.location.pathname);
    return m ? m[1] : null;
  }

  function oemRefineCacheKey(handle) {
    /* Versioned: the cached shape changed with each display redesign
       (v1 raw bar values → v2 tiers/words → v3 adds hasUndercoat → v4 adds
       effectCoat + the ×2–3 coats correction → v5: the lookup switched to
       group=false + the alias-code fallback match, Task 24c — the RESULT can
       differ for the same handle, so pre-24c entries must not survive). The
       version bump makes older-shape entries unreachable rather than
       mis-applied — a stale v2 entry applied by v3 code actually hid the
       undercoat step card on a tri-coat product (caught live during the
       step-cards verification). */
    return 'oemPaintCharRefine:v5:' + handle;
  }

  function oemReadRefineCache(handle) {
    try {
      var raw = window.sessionStorage.getItem(oemRefineCacheKey(handle));
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.expiresAt !== 'number' || Date.now() > parsed.expiresAt) return null;
      return parsed.data || null;
    } catch (e) {
      return null; /* sessionStorage unavailable (private mode / quota) — silent no-op */
    }
  }

  function oemWriteRefineCache(handle, data) {
    try {
      window.sessionStorage.setItem(oemRefineCacheKey(handle), JSON.stringify({
        data: data,
        expiresAt: Date.now() + OEM_REFINE_CACHE_TTL_MS
      }));
    } catch (e) { /* quota / private-mode — non-fatal, just skip the cache */ }
  }

  function oemPrefersReducedMotion() {
    return !!(isBrowser && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* Word/multiplier swap with a short fade (opacity transition lives in
   * assets/oem-pdp-blocks.css); instant under prefers-reduced-motion. */
  function oemSwapText(el, text) {
    if (!el || el.textContent === text) return;
    if (oemPrefersReducedMotion()) {
      el.textContent = text;
      return;
    }
    el.style.opacity = '0';
    setTimeout(function () {
      el.textContent = text;
      el.style.opacity = '';
    }, 200);
  }

  /* Fill/unfill the row's 5 pips. The CSS background-color transition on
   * .oem-pip (disabled under prefers-reduced-motion) makes the count change
   * read as a step animation. */
  function oemSetPips(item, tier) {
    var pips = item.querySelectorAll('.oem-pip');
    for (var i = 0; i < pips.length; i++) {
      pips[i].classList.toggle('oem-pip--filled', i < tier);
    }
  }

  /* Retarget the redesigned rows in place: filled-pip counts + tier words
   * (sparkle/difficulty, incl. their role="img" aria-labels — SR users get
   * the refined value too), the coats step cards' ×N multiplier + SR
   * sentence, and the undercoat step card's presence (step numbers are CSS
   * counters, so they renumber themselves). The undercoat badge row is
   * never touched, and d.hasUndercoat always mirrors the server-rendered
   * metafield truth — the card toggle is a consistency guarantee, not a V3
   * override. */
  function oemApplyRefinedDisplay(root, d) {
    var sparkle = root.querySelector('.oem-slider-item[data-char="sparkle"]');
    if (sparkle) {
      oemSetPips(sparkle, d.effectTier);
      sparkle.setAttribute('data-tier', String(d.effectTier));
      oemSwapText(sparkle.querySelector('[data-oem-tier-label]'), d.finishWord);
      var sparkleRow = sparkle.querySelector('.oem-pip-row');
      if (sparkleRow) {
        sparkleRow.setAttribute('aria-label',
          'Sparkle / effect intensity: ' + d.finishWord + ', level ' + d.effectTier + ' of 5');
      }
    }
    var difficulty = root.querySelector('.oem-slider-item[data-char="difficulty"]');
    if (difficulty) {
      oemSetPips(difficulty, d.difficultyTier);
      difficulty.setAttribute('data-tier', String(d.difficultyTier));
      oemSwapText(difficulty.querySelector('[data-oem-tier-label]'), d.difficultyWord);
      var difficultyRow = difficulty.querySelector('.oem-pip-row');
      if (difficultyRow) {
        difficultyRow.setAttribute('aria-label',
          'Application difficulty: ' + d.difficultyWord + ', level ' + d.difficultyTier + ' of 5');
      }
    }
    var coats = root.querySelector('.oem-slider-item[data-char="coats"]');
    if (coats) {
      coats.setAttribute('data-coats', d.coats);
      oemSwapText(coats.querySelector('[data-oem-color-coats]'), '×' + d.coats);
      /* Effect-coat sub-note: VISIBILITY toggle only — its text is a
         merchant-editable block setting and must never be rewritten. */
      if (typeof d.effectCoat === 'boolean') {
        var effectNote = coats.querySelector('[data-oem-effect-note]');
        if (effectNote) effectNote.hidden = !d.effectCoat;
      }
      /* Belt-and-braces on top of the versioned cache key: only touch the
         undercoat step / SR sentence when the display model actually
         carries the flag — a stale/foreign shape must never hide
         commerce-truth UI. */
      if (typeof d.hasUndercoat === 'boolean') {
        var underStep = coats.querySelector('[data-oem-under-step]');
        if (underStep) underStep.hidden = !d.hasUndercoat;
        var srEl = coats.querySelector('[data-oem-steps-sr]');
        if (srEl) srEl.textContent = oemCoatsSrText(d.coats, d.hasUndercoat, d.effectCoat);
      }
    }
    root.setAttribute('data-refined', 'true');
  }

  /* window.OEMSearchEngine may not exist yet when this runs: theme.liquid
   * places the <script defer src="oem-search-engine.js"> AFTER
   * {{ content_for_layout }} (bottom of <body>, a deliberate perf choice),
   * while THIS file's own <script defer> tag is rendered by the product
   * section, earlier in the document. Deferred scripts execute in document
   * order — both before DOMContentLoaded — so oem-pdp-blocks.js's own
   * bootstrap (readyState is already 'interactive', never 'loading', by the
   * time ANY deferred script runs — a pre-existing quirk of the shared
   * init() bootstrap below, harmless for this file's other init* calls
   * since none of them touch window.OEMSearchEngine) calls init()
   * synchronously, before oem-search-engine.js has executed. Bounded poll
   * (2s @ 50ms) rather than reordering shared script tags or the shared
   * bootstrap — smallest change that makes the defensive guard meaningful
   * instead of a permanent silent no-op. */
  function oemWaitForSearchEngine(cb, attemptsLeft) {
    if (attemptsLeft == null) attemptsLeft = 40;
    if (window.OEMSearchEngine && typeof window.OEMSearchEngine.searchColors === 'function') {
      cb();
      return;
    }
    if (attemptsLeft <= 0) {
      console.debug('[oem] window.OEMSearchEngine never became available — paint characteristics heuristic stands');
      return;
    }
    setTimeout(function () { oemWaitForSearchEngine(cb, attemptsLeft - 1); }, 50);
  }

  function initPaintCharacteristicsRefine() {
    var root = document.querySelector('.oem-sliders[data-oem-color-code]');
    if (!root) return; /* legacy mode (use_auto_values off), or block absent */
    if (root.getAttribute('data-refined') === 'true' || root.__oemRefineStarted) return;

    var colorCode = (root.getAttribute('data-oem-color-code') || '').trim();
    var brand = (root.getAttribute('data-oem-brand') || '').trim();
    if (!colorCode) return;

    var handle = oemProductHandleFromLocation();

    if (handle) {
      var cached = oemReadRefineCache(handle);
      if (cached && cached.finishWord && typeof cached.hasUndercoat === 'boolean' && typeof cached.effectCoat === 'boolean') {
        oemApplyRefinedDisplay(root, cached);
        console.debug('[oem] Paint characteristics refined from sessionStorage cache');
        return;
      }
    }

    root.__oemRefineStarted = true;
    var hasUndercoat = !!root.querySelector('.oem-undercoat-indicator');

    oemWaitForSearchEngine(function () {
      /* group:false (Task 24c): this lookup matches by EXACT primary code —
         the grouped browse list (group=true, the server default) collapses a
         paint's records into one rep whose primary code may be a sibling's
         (e.g. searching LY7C returns the Y7C rep), which would break the
         equality match. group=false returns the exact record. */
      window.OEMSearchEngine.searchColors({ q: colorCode, pageSize: 10, group: false })
        .then(function (result) {
          var colors = (result && result.colors) || [];
          var match = oemFindConfidentMatch(colors, colorCode, brand);
          if (!match) {
            console.debug('[oem] No confident V3 match for paint characteristics (' + colorCode + ' / ' + brand + ') — heuristic stands');
            return;
          }
          var display = oemComputeRefinedDisplay(match.finish, hasUndercoat);
          oemApplyRefinedDisplay(root, display);
          if (handle) oemWriteRefineCache(handle, display);
          console.debug('[oem] Paint characteristics refined from V3 match:', match.finish);
        })
        .catch(function (err) {
          console.debug('[oem] Paint characteristics refine failed (heuristic stands):', err);
        });
    });
  }

  /* ─── Master Init ─────────────────────────────────────────────── */

  function init(scope) {
    var root = scope || document;
    var container = root.querySelector ? root.querySelector('.tempests-pdp') : null;
    if (!container) container = root.querySelectorAll ? root : document;

    initDrawerPanels(container);
    initFAQAccordions(container);
    initColorSearchDrawer(container);
    initColorSearchInput(container);
    initUndercoatBundle();
    initVehicleCompat();
    initPaintCharacteristicsRefine();
  }

  /* ─── Bootstrap ───────────────────────────────────────────────── */

  if (isBrowser) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        init(document);
      });
    } else {
      init(document);
    }

    /* ─── Theme Editor Re-init ────────────────────────────────────── */

    document.addEventListener('shopify:section:load', function (e) {
      if (e.target) {
        init(e.target);
      }
    });
  }

  /* ── Info popovers ─────────────────────────────────────────────────────
   * Click an [data-oem-info] icon (slider / spec label) → its content shows in
   * a small dialog box positioned by the icon. One popover reused; dismissed on
   * outside-click, Escape, or scroll/resize. Content is set via textContent, so
   * merchant tooltip text can never inject markup. Keyboard: the icons carry
   * role="button" tabindex="0"; Enter/Space activate them (handled here). */
  if (isBrowser) {
    var oemInfoPop = null;
    var oemInfoBtn = null;
    var oemCloseInfo = function () {
      if (oemInfoPop) { oemInfoPop.remove(); oemInfoPop = null; }
      if (oemInfoBtn) { oemInfoBtn.setAttribute('aria-expanded', 'false'); oemInfoBtn = null; }
    };
    var oemOpenInfo = function (btn) {
      oemCloseInfo();
      var content = btn.getAttribute('data-oem-info');
      if (!content) return;
      var pop = document.createElement('div');
      pop.className = 'oem-info-pop';
      pop.setAttribute('role', 'tooltip');
      pop.textContent = content;
      document.body.appendChild(pop);
      var r = btn.getBoundingClientRect();
      var pr = pop.getBoundingClientRect();
      var top = r.bottom + 8;
      if (top + pr.height > window.innerHeight - 8 && r.top - pr.height - 8 > 0) top = r.top - pr.height - 8;
      var left = Math.max(8, Math.min(r.left + (r.width / 2) - (pr.width / 2), window.innerWidth - pr.width - 8));
      pop.style.top = (top + window.pageYOffset) + 'px';
      pop.style.left = (left + window.pageXOffset) + 'px';
      btn.setAttribute('aria-expanded', 'true');
      oemInfoPop = pop;
      oemInfoBtn = btn;
    };
    document.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-oem-info]') : null;
      if (btn) {
        e.preventDefault();
        e.stopPropagation();
        if (btn === oemInfoBtn) oemCloseInfo(); else oemOpenInfo(btn);
        return;
      }
      if (oemInfoPop && !(e.target.closest && e.target.closest('.oem-info-pop'))) oemCloseInfo();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { oemCloseInfo(); return; }
      if ((e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar')) {
        var btn = e.target.closest ? e.target.closest('[data-oem-info]') : null;
        if (btn) { e.preventDefault(); if (btn === oemInfoBtn) oemCloseInfo(); else oemOpenInfo(btn); }
      }
    });
    window.addEventListener('scroll', function () { if (oemInfoPop) oemCloseInfo(); }, true);
    window.addEventListener('resize', function () { if (oemInfoPop) oemCloseInfo(); });
  }

  /* ── Paint orb lightbox (owner 2026-07): click a mini-orb button → 600px view ── */
  if (isBrowser) {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-oem-orb]') : null;
      if (!btn) return;
      e.preventDefault();
      var src = btn.getAttribute('data-oem-orb');
      var fallback = btn.getAttribute('data-oem-orb-fallback') || '';
      var name = btn.getAttribute('aria-label') || '';
      name = name.replace(/^View\s+/, '').replace(/\s+in 3D$/, '');

      var box = document.createElement('div');
      box.className = 'oem-orb-lightbox';
      box.innerHTML =
        '<button type="button" class="oem-orb-lightbox__close" aria-label="Close">\u00d7</button>' +
        '<img class="oem-orb-lightbox__img" src="' + src + '" alt="' + name + ' paint orb">' +
        (name ? '<p class="oem-orb-lightbox__caption">' + name + '</p>' : '');
      var img = box.querySelector('.oem-orb-lightbox__img');
      if (fallback) img.onerror = function () { if (img.src !== fallback) img.src = fallback; };
      document.body.appendChild(box);
      requestAnimationFrame(function () { box.setAttribute('data-open', 'true'); });

      function close() {
        box.removeAttribute('data-open');
        setTimeout(function () { box.remove(); document.removeEventListener('keydown', onKey); }, 240);
      }
      function onKey(ev) { if (ev.key === 'Escape') close(); }
      box.addEventListener('click', function (ev) {
        if (ev.target === box || ev.target.closest('.oem-orb-lightbox__close')) close();
      });
      document.addEventListener('keydown', onKey);
    });
  }

  /* ── Inline accessories: mcc-new per-size composition ────────────────
     Ported 1:1 from mcc-new colorDetailPanel (ESSENTIALS_SIZE_FALLBACK +
     effectiveUpsellProducts): the displayed list = the selected variant's
     custom.upsell_list, then custom.oem_kit_products filtered by per-size
     title-regex rules (rule order = display order, first title match wins),
     then custom.product_upsell_list1 — deduped by product id. Pint/Quart/
     Gallon always offer the Standard Clearcoat if it's in the pool. */
  if (isBrowser) {
    var OEM_ACC_SIZE_RULES = {
      '4oz': [
        { m: /2k.*clearcoat.*aerosol/i, d: 'Protection + high gloss finish' },
        { m: /preval.*sprayer/i, d: 'Turns your 4oz bottle into a spray can' },
        { m: /grey.*scuff.*pad|scuff.*pad.*grey/i, d: 'Surface prep and scuffing' },
        { m: /epoxy.*primer.*aerosol|1kepoxy/i, d: 'For bare metal or raw plastic' },
        { m: /primer.*filler.*aerosol|xpressbuild/i, d: 'Fills scratches and imperfections' },
        { m: /adhesion.*promoter|ultragrip/i, d: 'For plastic or smooth surfaces' },
        { m: /matt.*clear.*aerosol|promatt/i, d: 'For a matte or satin finish instead' },
        { m: /masking tape.*1\.5/i, d: 'For clean paint lines' },
        { m: /masking tape.*3\/4/i, d: 'For tight curves and detail work' },
        { m: /tape and drape/i, d: 'Protect surrounding areas' },
        { m: /respirator/i, d: 'Protect yourself from fumes' }
      ],
      '2oz': [
        { m: /grey.*scuff.*pad|scuff.*pad.*grey/i, d: 'Surface prep and scuffing' },
        { m: /masking tape.*1\.5/i, d: 'For clean paint lines' },
        { m: /masking tape.*3\/4/i, d: 'For tight curves and detail work' },
        { m: /respirator/i, d: 'Protect yourself from fumes' }
      ],
      'Aerosol': [
        { m: /2k.*clearcoat.*aerosol/i, d: 'Protection + high gloss finish' },
        { m: /grey.*scuff.*pad|scuff.*pad.*grey/i, d: 'Surface prep and scuffing' },
        { m: /epoxy.*primer.*aerosol|1kepoxy/i, d: 'For bare metal or raw plastic' },
        { m: /primer.*filler.*aerosol|xpressbuild/i, d: 'Fills scratches and imperfections' },
        { m: /adhesion.*promoter|ultragrip/i, d: 'For plastic or smooth surfaces' },
        { m: /matt.*clear.*aerosol|promatt/i, d: 'For a matte or satin finish instead' },
        { m: /masking tape.*1\.5/i, d: 'For clean paint lines' },
        { m: /masking tape.*3\/4/i, d: 'For tight curves and detail work' },
        { m: /tape and drape/i, d: 'Protect surrounding areas' },
        { m: /respirator/i, d: 'Protect yourself from fumes' }
      ],
      'Pint': [
        { m: /standard.*clearcoat|clearcoat.*4\.7l/i, d: 'Good durability, affordable option' },
        { m: /vhs.*crystal.*clearcoat|clearcoat.*5l.*kit/i, d: 'Premium: deep gloss, superior leveling' },
        { m: /grey.*scuff.*pad|scuff.*pad.*grey/i, d: 'Surface prep and scuffing' },
        { m: /primer.*1\.25l/i, d: 'For bare metal, dents, or imperfections' },
        { m: /masking tape.*1\.5/i, d: 'For clean paint lines' },
        { m: /masking tape.*3\/4/i, d: 'For tight curves and detail work' },
        { m: /tape and drape/i, d: 'Protect surrounding areas' },
        { m: /respirator/i, d: 'Protect yourself from fumes' }
      ],
      'Quart': [
        { m: /standard.*clearcoat|clearcoat.*4\.7l/i, d: 'Good durability, affordable option' },
        { m: /vhs.*crystal.*clearcoat|clearcoat.*5l.*kit/i, d: 'Premium: deep gloss, superior leveling' },
        { m: /grey.*scuff.*pad|scuff.*pad.*grey/i, d: 'Surface prep and scuffing' },
        { m: /primer.*1\.25l/i, d: 'For bare metal, dents, or imperfections' },
        { m: /masking tape.*1\.5/i, d: 'For clean paint lines' },
        { m: /masking tape.*3\/4/i, d: 'For tight curves and detail work' },
        { m: /tape and drape/i, d: 'Protect surrounding areas' },
        { m: /respirator/i, d: 'Protect yourself from fumes' }
      ],
      'Gallon': [
        { m: /standard.*clearcoat|clearcoat.*4\.7l/i, d: 'Good durability, affordable option' },
        { m: /vhs.*crystal.*clearcoat|clearcoat.*5l.*kit/i, d: 'Premium: deep gloss, superior leveling' },
        { m: /grey.*scuff.*pad|scuff.*pad.*grey/i, d: 'Surface prep and scuffing' },
        { m: /primer.*4\.5l/i, d: 'For bare metal, dents, or imperfections' },
        { m: /masking tape.*1\.5/i, d: 'For clean paint lines' },
        { m: /masking tape.*3\/4/i, d: 'For tight curves and detail work' },
        { m: /tape and drape/i, d: 'Protect surrounding areas' },
        { m: /respirator/i, d: 'Protect yourself from fumes' }
      ]
    };
    var OEM_ACC_STD_CLEARCOAT_ID = 14685301047660;

    function oemAccNormalizeSize(raw) {
      if (!raw) return null;
      var v = String(raw).toLowerCase();
      if (v.indexOf('pint') !== -1) return 'Pint';
      if (v.indexOf('quart') !== -1) return 'Quart';
      if (v.indexOf('gallon') !== -1) return 'Gallon';
      if (v.indexOf('kit') !== -1) return 'Kit';
      if (v.indexOf('2oz') !== -1 || v.indexOf('2 oz') !== -1) return '2oz';
      if (v.indexOf('4oz') !== -1 || v.indexOf('4 oz') !== -1) return '4oz';
      if (v.indexOf('aerosol') !== -1 || v.indexOf('spray') !== -1) return 'Aerosol';
      return raw;
    }

    function oemAccCompose() {
      var el = document.querySelector('script[data-oem-acc-data]');
      if (!el) return null;
      var data;
      try { data = JSON.parse(el.textContent); } catch (e) { return null; }
      var idInput = document.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
      var vid = idInput && idInput.value ? String(idInput.value) : null;
      var variants = data.variants || [];
      var v = null;
      for (var i = 0; i < variants.length; i++) { if (String(variants[i].id) === vid) { v = variants[i]; break; } }
      if (!v && variants.length) v = variants[0]; /* nothing selected → first (mirrors first-available render) */
      if (!v) return null;
      var size = oemAccNormalizeSize(v.size);
      var out = [], seen = {};
      (v.upsells || []).forEach(function (it) { if (it && it.variantId && !seen[it.id]) { seen[it.id] = 1; out.push(it); } });
      var rules = OEM_ACC_SIZE_RULES[size];
      if (rules && (data.kitProducts || []).length) {
        rules.forEach(function (r) {
          for (var k = 0; k < data.kitProducts.length; k++) {
            var kp = data.kitProducts[k];
            if (kp && kp.variantId && r.m.test(kp.title || '')) {
              if (!seen[kp.id]) { seen[kp.id] = 1; kp = Object.assign({}, kp); kp.desc = r.d; out.push(kp); }
              break; /* first title match wins, mcc-new getRelevantKitProducts */
            }
          }
        });
      }
      (data.productUpsells || []).forEach(function (it) { if (it && it.variantId && !seen[it.id]) { seen[it.id] = 1; out.push(it); } });
      /* Owner 2026-07: the 1K Clearcoat Pen is retired from recommendations —
         drop it regardless of which source (upsell_list / kit / product) it came from. */
      out = out.filter(function (it) { return !/clearcoat pen/i.test(it.title || ''); });
      if (size === 'Pint' || size === 'Quart' || size === 'Gallon') {
        if (!seen[OEM_ACC_STD_CLEARCOAT_ID]) {
          var pool = (data.kitProducts || []).concat(data.productUpsells || []);
          for (var s2 = 0; s2 < pool.length; s2++) {
            if (pool[s2] && pool[s2].id === OEM_ACC_STD_CLEARCOAT_ID && pool[s2].variantId) { out.push(pool[s2]); break; }
          }
        }
      }
      return { items: out, colorCode: data.colorCode || '' };
    }

    function oemAccRender() {
      var wrap = document.querySelector('[data-oem-acc-variant-rows]');
      if (!wrap) return;
      var composed = oemAccCompose();
      if (!composed) return;
      wrap.innerHTML = '';
      if (!composed.items.length) {
        var note = document.createElement('p');
        note.className = 'oem-acc-empty';
        note.textContent = 'Accessories coming soon.';
        wrap.appendChild(note);
        return;
      }
      composed.items.forEach(function (it) {
        var row = document.createElement('div');
        row.className = 'oem-acc-row';
        var img = document.createElement('div');
        img.className = 'oem-acc-row__img';
        if (it.image) {
          var im = document.createElement('img');
          im.src = it.image; im.alt = it.title || ''; im.loading = 'lazy';
          img.appendChild(im);
        }
        var text = document.createElement('div');
        text.className = 'oem-acc-row__text';
        var title = document.createElement('p');
        title.className = 'oem-acc-row__title';
        title.textContent = it.title || '';
        text.appendChild(title);
        if (it.desc) {
          var desc = document.createElement('p');
          desc.className = 'oem-acc-row__desc';
          desc.textContent = it.desc;
          text.appendChild(desc);
        }
        var price = document.createElement('p');
        price.className = 'oem-acc-row__price';
        price.textContent = it.price || '';
        text.appendChild(price);
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'oem-acc-row__add';
        btn.setAttribute('data-oem-acc-add', '');
        btn.setAttribute('data-variant-id', it.variantId);
        if (composed.colorCode) btn.setAttribute('data-added-with', composed.colorCode);
        btn.textContent = 'Add';
        row.appendChild(img); row.appendChild(text); row.appendChild(btn);
        wrap.appendChild(row);
      });
    }

    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      if (e.target.closest('.ov-size, .sv-size')) setTimeout(oemAccRender, 300);
    });
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { setTimeout(oemAccRender, 400); });
    } else {
      setTimeout(oemAccRender, 400);
    }
  }

  /* ── Inline accessories: one-tap add (Recommended Accessories panel) ── */
  if (isBrowser) {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-oem-acc-add]') : null;
      if (!btn || btn.disabled) return;
      var vid = parseInt(btn.getAttribute('data-variant-id'), 10);
      if (!vid) return;
      btn.disabled = true;
      var orig = btn.textContent;
      btn.textContent = 'Adding…';
      var accItem = { id: vid, quantity: 1 };
      var addedWith = btn.getAttribute('data-added-with');
      if (addedWith) accItem.properties = { _added_with: addedWith }; /* hidden line-item prop, mcc.js convention */
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [accItem] })
      }).then(function (r) {
        if (!r.ok) throw new Error('add failed');
        return r.json();
      }).then(function () {
        btn.textContent = 'Added ✓';
        if (window.DYCToast) {
          var rowTitle = (btn.closest('.oem-acc-row') && btn.closest('.oem-acc-row').querySelector('.oem-acc-row__title')) || null;
          DYCToast.show({
            type: 'cart',
            title: 'Added to cart',
            desc: rowTitle ? rowTitle.textContent.trim() : 'Accessory added',
            actionText: 'View Cart',
            action: function () {
              var d = document.querySelector('cart-drawer');
              if (d && typeof d._refreshSections === 'function') d._refreshSections();
              if (d && typeof d.show === 'function') d.show();
            }
          });
        }
        fetch('/cart.js?_fd=0').then(function (r) { return r.json(); }).then(function (cart) {
          document.querySelectorAll('.cart-count-bubble span, [data-cart-count]').forEach(function (el) { el.textContent = cart.item_count; });
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'oem-accessories-inline', cartData: cart });
          }
        }).catch(function () {});
        setTimeout(function () { btn.textContent = orig; btn.disabled = false; }, 1800);
      }).catch(function () {
        btn.textContent = 'Try again';
        setTimeout(function () { btn.textContent = orig; btn.disabled = false; }, 1800);
      });
    });
  }

  /* Pure undercoat logic + node exports live in assets/oem-undercoat-atc.js.
   * Paint-characteristics-refine pure logic (Task 23) is exported here for
   * node --test (tests/oem-pdp-blocks.test.mjs), same isBrowser-gated pattern. */
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      OEM_PAINT_VALUES: OEM_PAINT_VALUES,
      OEM_DIFFICULTY_WORDS: OEM_DIFFICULTY_WORDS,
      brandsMatch: oemBrandsMatch,
      findConfidentMatch: oemFindConfidentMatch,
      computeRefinedValues: oemComputeRefinedValues,
      tierFromValue: oemTierFromValue,
      computeRefinedDisplay: oemComputeRefinedDisplay,
      coatsSrText: oemCoatsSrText
    };
  }
})();
